import { Bell, Building2, ChevronDown } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { YesNo } from '@/components/ui/button-group'
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { PhoneInput } from '@/components/ui/phoneInput'
import { Popover, PopoverContent, PopoverHeader, PopoverTitle, PopoverTrigger } from '@/components/ui/popover'
import { apiErrorDetail } from '@/lib/apiError'
import { useComplianceScope, useConfirmComplianceScope } from '@/models/vault'
import { useVault } from './VaultContext'

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** The confirmed scope beside the title, edited in place the way the quotes summary edits its answers. */
export function VaultScopeSummary() {
  const { orgId } = useVault()
  const { scope } = useComplianceScope(orgId)

  if (!scope?.dateConfirmed) return null

  return (
    <div className="flex flex-wrap items-center gap-1">
      <ScopePopover label="Business info" icon={<Building2 />} field="business" />
      <ScopePopover label="Notification settings" icon={<Bell />} field="notifications" />
    </div>
  )
}

type ScopeField = 'business' | 'notifications'

function ScopePopover({ label, icon, field }: { label: string; icon: ReactNode; field: ScopeField }) {
  const { staff } = useVault()
  const [isOpen, setIsOpen] = useState(false)

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-ink-600 hover:text-ink gap-2 max-md:w-full max-md:justify-between max-md:pr-2 max-md:pl-0"
        >
          <span className="max-md:hidden [&_svg]:size-4">{icon}</span>
          {label}
          <ChevronDown className="text-ink-400 size-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        collisionPadding={8}
        className="max-h-[70vh] w-96 max-w-[calc(100vw-1rem)] overflow-y-auto"
      >
        <PopoverHeader>
          <PopoverTitle>{staff ? label : `Update ${label.toLowerCase()}`}</PopoverTitle>
        </PopoverHeader>
        <ScopeForm field={field} readonly={staff} onSaved={() => setIsOpen(false)} />
      </PopoverContent>
    </Popover>
  )
}

/** Either half of the scope. Both save the whole thing, since confirming needs every answer. */
function ScopeForm({
  field,
  readonly,
  onSaved,
}: {
  field: ScopeField
  readonly?: boolean
  onSaved: () => void
}) {
  const { orgId } = useVault()
  const { scope, draft } = useComplianceScope(orgId)
  const confirm = useConfirmComplianceScope(orgId)
  const [interstate, setInterstate] = useState<boolean | null>(scope?.interstate ?? null)
  const [hazmat, setHazmat] = useState<boolean | null>(scope?.hazmat ?? null)
  const [emails, setEmails] = useState(
    (scope?.notificationEmails.length ? scope.notificationEmails : (draft?.contacts ?? [])).join(', ')
  )
  const [phone, setPhone] = useState(scope?.notificationPhones[0] ?? '')

  const addresses = emails
    .split(',')
    .map((address) => address.trim())
    .filter(Boolean)
  const emailsValid = addresses.length > 0 && addresses.every((address) => EMAIL.test(address))
  const answered = interstate !== null && hazmat !== null

  const save = () => {
    confirm.mutate(
      {
        interstate,
        hazmat,
        notificationEmails: addresses,
        notificationPhones: phone.trim() ? [phone.trim()] : [],
      },
      {
        onSuccess: () => {
          toast.success('Saved.')
          onSaved()
        },
        onError: (error) => toast.error(apiErrorDetail(error, 'Could not save that.')),
      }
    )
  }

  return (
    <div className="mt-4 flex flex-col gap-5">
      <FieldGroup>
        {field === 'business' ? (
          <>
            <Field role="group" aria-label="Interstate">
              <FieldLabel>Do you run interstate?</FieldLabel>
              <YesNo
                value={interstate}
                onChange={readonly ? () => undefined : setInterstate}
                className="w-40"
              />
            </Field>
            <Field role="group" aria-label="Hazmat">
              <FieldLabel>Do you haul hazmat?</FieldLabel>
              <YesNo value={hazmat} onChange={readonly ? () => undefined : setHazmat} className="w-40" />
            </Field>
          </>
        ) : (
          <>
            <Field data-invalid={emails.length > 0 && !emailsValid}>
              <FieldLabel htmlFor="scope-emails">Email</FieldLabel>
              <Input
                id="scope-emails"
                value={emails}
                disabled={readonly}
                onChange={(event) => setEmails(event.target.value)}
                aria-invalid={emails.length > 0 && !emailsValid}
              />
              {emails.length > 0 && !emailsValid ? (
                <FieldError>That does not look like an email address.</FieldError>
              ) : (
                <FieldDescription>Separate several addresses with commas.</FieldDescription>
              )}
            </Field>
            <Field>
              <FieldLabel htmlFor="scope-phone" className="gap-2">
                Phone number
                <Badge variant="secondary">Coming soon</Badge>
              </FieldLabel>
              {/* Stored, but nothing texts off it yet: the chase digest is email only. */}
              <PhoneInput id="scope-phone" disabled value={phone} onChange={(next) => setPhone(next || '')} />
            </Field>
          </>
        )}
      </FieldGroup>
      {!readonly && (
        <Button
          className="w-fit"
          disabled={!answered || !emailsValid}
          loading={confirm.isPending ? 'Saving…' : undefined}
          onClick={save}
        >
          Save
        </Button>
      )}
    </div>
  )
}
